import { ref } from 'vue'
import { sickLeaveApi } from '../services/sickLeaveApi.js'

export function useSickLeave() {
  const monthlySickLeaves = ref({})
  const loadingSickLeaves = ref(false)

  /**
   * Load sick leaves for specific users and month
   * @param {Array<number>} userIds - Array of user IDs to load sick leaves for
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   */
  async function loadSickLeaves(userIds, year, month) {
    try {
      loadingSickLeaves.value = true
      const userSickLeaves = {}
      
      for (const userId of userIds) {
        try {
          const sickLeaves = await sickLeaveApi.getSickLeaves(userId, year, month)
          if (sickLeaves && sickLeaves.length > 0) {
            userSickLeaves[userId] = sickLeaves
          }
        } catch (error) {
          console.warn(`Could not load sick leaves for user ${userId}:`, error)
        }
      }
      
      monthlySickLeaves.value = userSickLeaves
      return userSickLeaves
    } catch (error) {
      console.error('Failed to load sick leaves:', error)
      monthlySickLeaves.value = {}
      return {}
    } finally {
      loadingSickLeaves.value = false
    }
  }

  /**
   * Check if a user has sick leave on a specific date
   * @param {number} userId - User ID
   * @param {string|Date} date - Date to check (YYYY-MM-DD string or Date object)
   * @returns {boolean}
   */
  function hasSickLeaveOnDate(userId, date) {
    if (!userId) {
      return false
    }
    
    const sickLeaves = monthlySickLeaves.value[userId]
    if (!sickLeaves || sickLeaves.length === 0) {
      return false
    }
    
    // Normalize date to YYYY-MM-DD format
    let dateStr
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0]
    } else if (typeof date === 'string') {
      // If it's already a string, try to normalize it
      dateStr = date.split('T')[0]
    } else {
      return false
    }
    
    // Check against start_date field from API
    return sickLeaves.some(sl => sl.start_date === dateStr)
  }

  /**
   * Get all sick leaves for a specific user
   * @param {number} userId - User ID
   * @returns {Array} Array of sick leave objects
   */
  function getSickLeavesForUser(userId) {
    return monthlySickLeaves.value[userId] || []
  }

  /**
   * Clear all loaded sick leaves
   */
  function clearSickLeaves() {
    monthlySickLeaves.value = {}
  }

  return {
    monthlySickLeaves,
    loadingSickLeaves,
    loadSickLeaves,
    hasSickLeaveOnDate,
    getSickLeavesForUser,
    clearSickLeaves
  }
}
